import React, { useEffect, useMemo, useState, useContext } from 'react';
import { Button, Drawer, Space, Table, Typography, Card, Tooltip, Popconfirm, Input, Switch } from 'antd';
import { openNotification } from '../../../utils';
import { HolderOutlined, SearchOutlined } from '@ant-design/icons';
import TrashIcon from 'assets/icons/trash.svg';
import PenIcon from 'assets/icons/pen.svg';
import { listGroupsAll, deleteGroup, reorderGroups, updateGroup } from '../services/groupService';
import GroupForm from './Form';
import { DndContext, DragOverlay } from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, arrayMove, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

export default function GroupList() {
  const [data, setData] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | undefined>();
  const [search, setSearch] = useState('');
  const [activeId, setActiveId] = useState<string | null>(null);
  const [showInactive, setShowInactive] = useState<boolean>(false);

  const fetchData = async () => {
    const query = showInactive ? '' : 'isActive=true';
    const res = await listGroupsAll(query);
    const list = Array.isArray(res) ? res : [];
    const sorted = list.slice().sort((a: any, b: any) => (Number(a?.sortOrder || 0) - Number(b?.sortOrder || 0)));
    const filtered = search
      ? sorted.filter((g: any) => (g?.name || '').toString().toLocaleLowerCase('tr').includes(search.toLocaleLowerCase('tr')))
      : sorted;
    setData(filtered);
  };
  useEffect(() => { fetchData(); }, [search, showInactive]);
  const openCreate = () => { setEditingId(undefined); setOpen(true); };
  const openEdit = (id: number) => { setEditingId(id); setOpen(true); };
  const handleClose = () => setOpen(false);
  const handleDelete = async (id: number) => {
    try {
      await deleteGroup(id);
      openNotification('success', 'Başarılı', 'Kategori Silindi');
      fetchData();
    } catch (e) {
      openNotification('error', 'Hata', 'Kategori silme işlemi başarısız');
    }
  };

  // Drag & drop ordering (dnd-kit)
  const RowContext = React.createContext<{ setActivatorNodeRef?: (el: HTMLElement | null) => void; listeners?: any }>({});
  const DragHandle: React.FC = () => {
    const { setActivatorNodeRef, listeners } = useContext(RowContext);
    return (
      <HolderOutlined ref={setActivatorNodeRef} {...listeners} style={{ cursor: 'move', color: '#999' }} />
    );
  };

  const RowComponent = (props: any) => {
    const { children, ...restProps } = props;
    const record = children?.[0]?.props?.record || {};
    const key = String(record?.id ?? restProps['data-row-key'] ?? 'no-key');

    const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id: key });
    const style: React.CSSProperties = {
      ...restProps.style,
      transform: CSS.Transform.toString(transform),
      transition,
      ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
    };

    const value = useMemo(() => ({ setActivatorNodeRef, listeners }), [setActivatorNodeRef, listeners]);

    return (
      <RowContext.Provider value={value}>
        <tr {...restProps} ref={setNodeRef} style={style} {...attributes}>
          {children}
        </tr>
      </RowContext.Provider>
    );
  };

  const onDragEnd = async ({ active, over }: DragEndEvent) => {
    setActiveId(null);
    if (!over || active.id === over.id) return;
    const oldIndex = data.findIndex((item: any) => String(item.id) === String(active.id));
    const newIndex = data.findIndex((item: any) => String(item.id) === String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;
    const newData = arrayMove(data, oldIndex, newIndex);
    const reordered = newData.map((item: any, index: number) => ({ id: item.id, sortOrder: index + 1 }));
    setData((prev) => newData.map((item: any, index: number) => ({ ...item, sortOrder: index + 1 })));
    try {
      await reorderGroups(reordered);
      openNotification('success', 'Başarılı', 'Sıralama güncellendi');
    } catch (e) {
      openNotification('error', 'Hata', 'Sıralama güncellenemedi');
      fetchData();
    }
  };

  const sortableItems = useMemo(() => data.map((d: any) => String(d.id)), [data]);

  const columns = [
    {
      key: 'drag',
      width: 40,
      align: 'center' as const,
      render: () => <DragHandle />,
    },
    { title: 'Kategori Adı', dataIndex: 'name' },
    {
      title: 'Durum',
      dataIndex: 'isActive',
      render: (v: any, r: any) => (
        <Switch
          checked={!!v}
          onChange={async (val) => {
            try {
              await updateGroup(r.id, { ...r, isActive: val });
              setData(prev => prev.map(item => item.id === r.id ? ({ ...item, isActive: val }) : item));
              openNotification('success', 'Başarılı', val ? 'Kategori aktif' : 'Kategori pasif');
            } catch (e) {
              openNotification('error', 'Hata', 'Durum güncellenemedi');
            }
          }}
        />
      )
    },
    {
      title: 'İşlemler', render: (_: any, r: any) => (
        <Space>
          <Tooltip title='Düzenle'>
            <Button
              icon={<img src={PenIcon} alt="pen" className="icon" />}
              shape="circle"
              onClick={() => openEdit(r.id)}
              className="icon-button"
            />
          </Tooltip>
          <Popconfirm title='Sil?' onConfirm={() => handleDelete(r.id)}>
            <Tooltip title='Sil'>
              <Button
                icon={<img src={TrashIcon} alt="trash" className="icon" />}
                shape="circle"
                className="icon-button ml-12px"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <>
      <Card
        className='br-30px kontrolx-card'
        title={(
          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <Typography.Title level={4} style={{ margin: 0 }}>Kategori Listesi</Typography.Title>
            <Typography.Text type='secondary' className='ms-5' style={{ fontWeight: 500 }}>
              <strong>({data.length})</strong> kayıt listelendi
            </Typography.Text>
          </div>
        )}
        extra={
          <Space>
                        <Space>
              <span>Pasifleri göster</span>
              <Switch checked={showInactive} onChange={(v)=>setShowInactive(v)} />
            </Space>
            <Input
              allowClear
              placeholder="Ara"
              prefix={<SearchOutlined style={{ color: '#b8b9bc' }} />}
              className="d-input d-input-form mr-16px d-input-filter"
              style={{ width: 200, marginRight: 16 }}
              value={search} onChange={(e) => setSearch(e.target.value)}
            />

            <Button type='primary' onClick={openCreate}>Yeni Kategori Ekle</Button>
          </Space>
        }
      >
        <DndContext
          modifiers={[restrictToVerticalAxis]}
          onDragEnd={onDragEnd}
          onDragStart={({ active }: any) => setActiveId(String(active.id))}
        >
          <SortableContext items={sortableItems} strategy={verticalListSortingStrategy}>
            <Table
              rowKey='id'
              dataSource={data}
              columns={columns}
              pagination={false}
              components={{ body: { row: RowComponent } }}
            />
          </SortableContext>
          <DragOverlay>
            {activeId ? (
              <div style={{ padding: '4px 8px', background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.1)', borderRadius: 4 }}>
                {data.find((d: any) => String(d.id) === String(activeId))?.name}
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      </Card>
      <Drawer open={open} onClose={handleClose} destroyOnClose width={480} title={editingId ? 'Kategori Düzenle' : 'Kategori Oluştur'}>
        <GroupForm id={editingId} onSuccess={() => { handleClose(); fetchData(); }} />
      </Drawer>
    </>
  );
}
